import {
  calendar,
  cart,
  cash,
  checkmark,
  eye,
  flag,
  flame,
  hammer,
  handLeft,
  heart,
  leaf,
  location,
  megaphone,
  newspaper,
  people,
  pricetag,
  rose,
  time,
  timer,
  trophy,
} from 'ionicons/icons';
import type { TopicType, TopicTypeId } from '@/modules/topic/types';

/**
 * How a topic type presents itself on the type picker's sticker card: a main
 * icon (the big tilted sticker, also used in the type tabs), two accent icons
 * stuck around it, and a short tagline under the type's name. The sticker's
 * colours come from the `--app-topic-<id>` tokens (see
 * `TopicTypePickerSheet.module.css`), keyed by the same id.
 */
export interface TopicTypeSticker {
  icon: string;
  accentIcons: readonly [string, string];
  tagline: string;
  /**
   * The examples in the "Think:" line along the bottom of the card. Omitted when the type's
   * comma-separated `description` already lists good examples.
   */
  examples?: readonly string[];
}

export const TOPIC_TYPE_STICKERS: Readonly<
  Record<TopicTypeId, TopicTypeSticker>
> = {
  beautification: {
    icon: leaf,
    accentIcons: [rose, hammer],
    tagline: 'Glow up the block',
  },
  challenge: {
    icon: flame,
    accentIcons: [trophy, timer],
    tagline: 'Dare your crew',
  },
  deal: {
    icon: pricetag,
    accentIcons: [cash, cart],
    tagline: 'Bargains near you',
  },
  event: {
    icon: calendar,
    accentIcons: [time, location],
    tagline: 'Save the date',
    examples: ['Has a date', 'Has a time', 'Has a place'],
  },
  rally: {
    icon: megaphone,
    accentIcons: [flag, people],
    tagline: 'Show up, speak out',
    examples: ['March', 'Gathering', 'Show of support'],
  },
  news: {
    icon: newspaper,
    accentIcons: [eye, checkmark],
    tagline: 'Be a trusted source',
    examples: ['Eye witness report', 'Opinion piece', 'Verifiable provenance'],
  },
  volunteering: {
    icon: heart,
    accentIcons: [handLeft, people],
    tagline: 'Lend a hand',
  },
};

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The examples for a type: the sticker's own list, or else the type's
 * description split on commas, each starting with a capital.
 */
export function stickerExamples(type: TopicType): string[] {
  const raw =
    TOPIC_TYPE_STICKERS[type.id].examples ?? type.description.split(',');
  return raw.map(s => capitalize(s.trim())).filter(Boolean);
}

/** The picker's call to action for a type, e.g. "Start an Event Topic". */
export function startTopicLabel(type: TopicType): string {
  const article = /^[aeiou]/i.test(type.choiceLabel) ? 'an' : 'a';
  return `Start ${article} ${type.choiceLabel} Topic`;
}
